import {
  CAMERA_DEFAULT_Z,
  CAMERA_MAX_ZOOM,
  CAMERA_MIN_ZOOM,
  PLANE_DEFAULT_Z,
} from '@Components/BigBrowse/BigBrowseConfig';
import { extend, useFrame, useThree } from '@react-three/fiber';
import CameraControls from 'camera-controls';
import { THREE } from 'expo-three';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import type { CanvasManager } from '../CanvasManager';

type Props = {
  canvasManager: CanvasManager;
};

CameraControls.install({ THREE });
extend({ CameraControls });

export const CameraControls2 = observer(function CameraControls2({
  canvasManager,
}: Props) {
  const {
    camera,
    gl: { domElement },
  } = useThree();

  const controls = useRef<CameraControls>(null);
  useFrame((_, delta) => controls.current?.update(delta));

  const getIfCameraIsOutOfBoundary = ([x, y, z]: [number, number, number]) => {
    if (!canvasManager.scrollBoundary) {
      return false;
    }

    const { top, left, right, bottom } = canvasManager.scrollBoundary;
    return (
      x < left || x > right || y > top || y < bottom || z !== CAMERA_DEFAULT_Z
    );
  };

  const getPositionInsideBoundary = ([x, y]: [number, number, number]): [
    number,
    number,
    number
  ] => {
    const SMALL_SHIFT = 3;
    const { top, left, right, bottom } = canvasManager.cameraBoundaries;

    const rX = Math.min(Math.max(left + SMALL_SHIFT, x), right - SMALL_SHIFT);
    const rY = Math.max(Math.min(top - SMALL_SHIFT, y), bottom + SMALL_SHIFT);

    return [rX, rY, CAMERA_DEFAULT_Z];
  };

  useEffect(() => {
    const updateCameraProps = ({ target }: { target: CameraControls }) => {
      canvasManager.cameraZoom.set(target.camera.zoom);

      const { x, y, z } = target.camera.position;

      // Bugfix: when scrolling to boundary Y near to 0, when the camera value drops below 0.0001,
      // troika text freaks out and all text is disappearing
      // The following makes sure that we don't get close to 0.000x Y position
      if (Math.abs(y) < 0.1) {
        controls.current?.truck(0, 1, false);
      }

      // If scroll boundary is enabled it moves camera to arbitrary position
      // With the following we try to reset it to a known state.
      // Known state is the latest position in cameraPosition observable
      // Also when resized and being on the scrollBoundary
      // the camera gets outside the scroll boundary and displays nothing
      // we force the camera to come back in, and trigger a small scroll (truck command)
      // to force it update
      if (z !== CAMERA_DEFAULT_Z || getIfCameraIsOutOfBoundary([x, y, z])) {
        const [cX, cY, cZ] = canvasManager._cameraPositionThree.value;
        const [rX, rY] = getPositionInsideBoundary([cX, cY, cZ]);

        controls.current?.setLookAt(
          rX,
          rY,
          CAMERA_DEFAULT_Z,
          rX,
          rY,
          PLANE_DEFAULT_Z,
          false
        );

        controls.current?.truck(1, 1, false);

        canvasManager._cameraPositionThree.set([rX, rY, CAMERA_DEFAULT_Z]);
        return;
      }

      canvasManager._cameraPositionThree.set([x, y, z]);
    };

    // @ts-expect-error
    controls.current?.addEventListener('update', updateCameraProps);

    resetCamera().then();

    return () => {
      // @ts-expect-error
      controls.current?.removeEventListener('update', updateCameraProps);
    };
  }, []);

  const resetCamera = async () => {
    controls.current?.setLookAt(
      0,
      0,
      CAMERA_DEFAULT_Z,
      0,
      0,
      PLANE_DEFAULT_Z,
      false
    );
  };

  useEffect(() => {
    if (!canvasManager.scrollBoundary) {
      return controls.current?.setBoundary(undefined);
    }

    const { top, left, right, bottom } = canvasManager.scrollBoundary;
    const [cX, cY] = canvasManager._cameraPositionThree.value;

    controls.current?.setBoundary(
      new THREE.Box3(
        new THREE.Vector3(left, bottom, PLANE_DEFAULT_Z),
        new THREE.Vector3(right, top, CAMERA_DEFAULT_Z)
      )
    );

    // setting Scroll Boundary will cause camera to scroll
    // effectively we cancel it here
    controls.current?.setLookAt(
      cX,
      cY,
      CAMERA_DEFAULT_Z,
      cX,
      cY,
      PLANE_DEFAULT_Z,
      false
    );
  }, [canvasManager.scrollBoundary]);

  useEffect(() => {
    canvasManager.bindCameraControlsRef(controls);
  }, [controls.current]);

  const zoomAction = canvasManager.pinchZoomEnabled
    ? CameraControls.ACTION.TOUCH_ZOOM
    : CameraControls.ACTION.NONE;

  return (
    <cameraControls
      args={[camera, domElement]}
      ref={controls}
      boundaryEnclosesCamera
      mouseButtons={{
        left: CameraControls.ACTION.TRUCK,
        wheel: CameraControls.ACTION.TRUCK,
        middle: CameraControls.ACTION.NONE,
        right: CameraControls.ACTION.NONE,
        // @ts-expect-error
        shiftLeft: CameraControls.ACTION.NONE,
      }}
      touches={{
        one: CameraControls.ACTION.TOUCH_TRUCK,
        two: zoomAction,
        three: CameraControls.ACTION.NONE,
      }}
      minZoom={CAMERA_MIN_ZOOM}
      maxZoom={CAMERA_MAX_ZOOM}
    />
  );
});
